/* eslint-disable no-nested-ternary */
/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable complexity */
// 9fbef606107a605d69c0edbcd8029e5d
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Head from 'next/head';
import { getImgBackgroundStyleObjForBrand } from '@tcp/core/src/utils/utils.web';
import LabelsContext from '@tcp/core/src/components/common/context/LabelsContext';
import { getViewportInfo } from '@tcp/core/src/utils';
import useHasMounted from '@tcp/core/src/hooks/useHasMounted';
import ProductImageOverlay from './styles/ProductImage.style';
import PlpTileAnchor from './PlpTileAnchor';
import {
  getImageSourceString,
  getImageSourceStringAPLPNewTrans,
} from './plpTileHelpers/imageDataHelper';
import TopRatedBadges from './TopRatedBadges';
import plpTileLocators from './plpTileLocators';
import ColorSwatches from './InteractiveColorSwatch.view';

const getHoverProps = ({
  isMountedInteractiveColorSwatchBandEnabled,
  handleMouseEnter,
  handleMouseLeave,
}) => {
  return !isMountedInteractiveColorSwatchBandEnabled
    ? {
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
      }
    : {};
};

export const resolveMediaAssetUrl = (url, getImgUrl) => {
  if (!url) return '';
  const isVideoAsset = /\.(mp4|mov|webm|gif)$/i.test(url);
  const removeExtension = (mediaUrl) => mediaUrl.replace(/\.\w{3,4}$/i, '');
  return getImgUrl(isVideoAsset ? removeExtension(url) : url);
};

// eslint-disable-next-line sonarjs/cognitive-complexity
const ProductImage = (props) => {
  const {
    pdpUrl,
    imageBorder,
    itemAvailability,
    imageUrl,
    orgImageUrl,
    hoverImageUrl,
    overlayBadgeParams,
    productName,
    pathName,
    setProductDataForPdp,
    assetHostBaseUrl,
    topRatedBadge,
    bestSellerBadge,
    mostFavoritedBadge,
    preloadImage,
    aplpImageUrl,
    isAlternatePlpEnabled,
    isMountedTileRedesignAB,
    handleMouseEnter,
    handleMouseLeave,
    imageConfigWithoutDPR,
    isSLPTile,
    isMountedInteractiveColorSwatchBandEnabled,
    handleHoverOnColorSwatch,
    productPartNo,
    TCPSwatches,
    onQuickViewOpenClick,
    assetHost: assetHostProp,
    assetPath,
    saveDataForQv,
    swatchImg,
    tcpColor,
    auxDescription,
    relatedSwatchImages,
    isBundleProduct,
    handleViewBundleClick,
    isNewSwatchUrlEnabled,
    isHeroProduct,
    productIndex,
    isRandomBgColorEnabled,
    isFadeImageHoverEnabled,
  } = props;
  const [secondaryLoaded, setSecondaryLoaded] = useState(false);
  const { isDesktop } = getViewportInfo() || {};

  let source = imageUrl;
  const isVideoUrl = /(mp4|mov|webm|gif)$/i.test(imageUrl);

  const overlayParamaString = overlayBadgeParams ? `${overlayBadgeParams}/` : '';
  const compression = ',f_auto,q_auto';
  if (isVideoUrl) {
    source = imageUrl.replace(/\.\w{3,4}$/i, '');
  }

  const getMediaStringForPreload = (transformations, index, isDamImageV2EnabledPLP) => {
    if (isDamImageV2EnabledPLP) return `(min-width: ${transformations[index]?.width})`;
    if (
      transformations[index]?.name === 't_plp_img_t' ||
      transformations[index]?.name === 't_plp_img_m'
    ) {
      return `(min-width: ${transformations[index]?.width}) and (max-width: ${
        parseInt(transformations[index - 1]?.width?.replace('px', ''), 10) - 0.1
      }px)`;
    }

    return `(min-width: ${transformations[index]?.width})`;
  };

  const getSrcSet = (src, srcUrl) => {
    const { extraConfig } = React.useContext(LabelsContext);
    const { isDamImageV2EnabledPLP } = extraConfig || {};
    const transformations = isDamImageV2EnabledPLP
      ? [{ name: 't_plp_img_t', width: '0px' }]
      : [
          { name: 't_plp_img_d', width: '1200px' },
          { name: 't_plp_img_t', width: '768px' },
          { name: 't_plp_img_m', width: '0px' },
        ];

    let srcSetString = [];
    return transformations.map((t, index) => {
      if (aplpImageUrl) {
        srcSetString = getImageSourceStringAPLPNewTrans({
          aplpImageUrl,
          tname: t.name,
          isDamImageV2EnabledPLP,
        });
      } else {
        srcSetString = getImageSourceString({
          assetHostBaseUrl,
          overlayParamaString,
          compression,
          tname: t.name,
          src,
          isDamImageV2EnabledPLP,
          imageConfigWithoutDPR,
          isHeroProduct,
        });
      }

      return (
        <>
          {preloadImage && (
            <Head>
              <link
                rel="preload"
                as="image"
                href={srcUrl}
                imagesrcset={srcSetString}
                media={getMediaStringForPreload(transformations, index, isDamImageV2EnabledPLP)}
              />
            </Head>
          )}
          <source key={t.name} srcSet={srcSetString} media={`(min-width: ${t.width})`} />
        </>
      );
    });
  };

  const getImgUrl = (src) => {
    if (isVideoUrl) {
      return `${assetHostBaseUrl}/video/upload/${overlayParamaString}fl_animated,fl_awebp,e_loop,w_256,q_75,vs_100/${src}.webp`;
    }
    return `${assetHostBaseUrl}/image/upload/${overlayParamaString}t_plp_img_m${compression}/${src}`;
  };

  const { extraConfig } = React.useContext(LabelsContext);
  const { addBgdToTransparentImgEnabled, backgroundColorsForPng } = extraConfig || {};
  // prevent from being applied to videos and Alt PLP pages
  const backgroundStyle =
    !isVideoUrl && !aplpImageUrl
      ? getImgBackgroundStyleObjForBrand(
          addBgdToTransparentImgEnabled,
          backgroundColorsForPng,
          source,
          productIndex,
          isRandomBgColorEnabled
        )
      : null;
  const srcUrl = getImgUrl(source);

  const orgSrcUrl = resolveMediaAssetUrl(orgImageUrl, getImgUrl);
  const altSrcUrl = hoverImageUrl ? resolveMediaAssetUrl(hoverImageUrl, getImgUrl) : orgSrcUrl;

  let srcAPLPUrl = '';
  if (aplpImageUrl) {
    const {
      assetHost,
      altImage,
      assetSuffix,
      productBrand,
      productIdNumber,
      badgeNumber,
      badgeTransformation,
      imageBorderAPLP,
      imageName,
      imageBgColor,
      flLayer,
      west,
      northEast,
      southEast,
    } = aplpImageUrl;
    srcAPLPUrl = `${assetHost}/t_aplp_img,c_scale,f_auto,q_auto,dpr_1.0${imageBgColor}${flLayer},t_plp_img_m,f_auto,q_auto,h_1.0,x_2${west}${assetSuffix}${productBrand}:${productIdNumber}:${imageName}${flLayer},t_plp_img_m,f_auto,q_auto,h_1.0,${imageBorderAPLP}${west}${
      altImage[0]
        ? `${imageBgColor}/t_plp_img_m,f_auto,q_auto,w_0.49${flLayer}${northEast},x_1,y_1${assetSuffix}${productBrand}:${productIdNumber}:${altImage[0]}/t_plp_img_m,f_auto,q_auto,w_0.49,${imageBorderAPLP}${flLayer}${northEast}`
        : ''
    }${
      altImage[1]
        ? `${imageBgColor}/t_plp_img_m,f_auto,q_auto,w_0.49${flLayer}${southEast},x_1,y_1${assetSuffix}${productBrand}:${productIdNumber}:${altImage[1]}/t_plp_img_m,f_auto,q_auto,w_0.49,${imageBorderAPLP}${flLayer}${southEast}`
        : ''
    }${
      badgeNumber ? `/${badgeTransformation}${badgeNumber}${west},w_0.14,fl_relative` : ''
    }/bg/gym.jpg`;
    // src image for APLP with scale
    // const srcAPLPUrlWithScale = `${assetHost}/w_788,h_649,c_scale,f_auto,q_auto${altImage[0]?`${assetSuffix}${productBrand}:${productIdNumber}:${altImage[0]}/${transformation}${assetPrefix}north_east`:''}${altImage[1]?`${assetSuffix}${productBrand}:${productIdNumber}:${altImage[1]}/${transformation}${assetPrefix}south_east`:''}${assetSuffix}${productBrand}:${productIdNumber}:${productId}.jpg/${transformation},f_auto,q_auto,${imageBorderAPLP},fl_layer_apply,g_west${badgeNumber ? `/${badgeTransformation}${badgeNumber},g_west,w_0.14,fl_relative` : ''}/w_312/bg/gym.jpg`
  }
  const hasMounted = useHasMounted();

  useEffect(() => {
    const img = new Image();
    img.src = altSrcUrl;
    img.onload = () => setSecondaryLoaded(true);
  }, [altSrcUrl]);

  const hoverProps = getHoverProps({
    isMountedInteractiveColorSwatchBandEnabled,
    handleMouseEnter,
    handleMouseLeave,
  });
  const renderImage = ({ src, alt, className, loading, style, ...rest }) => (
    <img src={src} alt={alt} className={className} loading={loading} style={style} {...rest} />
  );
  return (
    <div className="relative">
      <div
        className={`${isAlternatePlpEnabled ? 'alt-product-image' : 'product-image'} ${
          isFadeImageHoverEnabled && hasMounted && isDesktop ? 'image-hover-fade' : ''
        }`}
        data-locator={plpTileLocators.productImage}
        {...hoverProps}
      >
        <PlpTileAnchor
          pathName={pathName}
          pdpUrl={pdpUrl}
          title={productName}
          setProductDataForPdp={setProductDataForPdp}
          isSLPTile={isSLPTile}
          fromProductImage
        >
          <picture>
            {!isFadeImageHoverEnabled && !isVideoUrl ? getSrcSet(source, srcUrl) : null}
            {isFadeImageHoverEnabled && hasMounted && isDesktop && orgSrcUrl !== altSrcUrl ? (
              <>
                {renderImage({
                  src: orgSrcUrl,
                  alt: productName,
                  className: 'primary-image',
                  loading: 'eager',
                  style: backgroundStyle,
                })}
                {secondaryLoaded &&
                  renderImage({
                    src: altSrcUrl,
                    alt: productName,
                    className: 'secondary-image',
                    loading: 'lazy',
                    style: backgroundStyle,
                  })}
              </>
            ) : (
              <>
                {!isVideoUrl ? getSrcSet(source, srcUrl) : null}
                {renderImage({
                  src: isAlternatePlpEnabled ? srcAPLPUrl : srcUrl,
                  alt: productName,
                  className: imageBorder,
                  loading: 'lazy',
                  ...(backgroundStyle ? { style: backgroundStyle } : {}),
                })}
              </>
            )}
          </picture>
        </PlpTileAnchor>
        {itemAvailability?.isSoldOut && (
          <ProductImageOverlay>{itemAvailability?.message}</ProductImageOverlay>
        )}
      </div>
      {!isMountedTileRedesignAB && (
        <TopRatedBadges
          mostFavoritedBadge={mostFavoritedBadge}
          topRatedBadge={topRatedBadge}
          bestSellerBadge={bestSellerBadge}
        />
      )}
      {isMountedInteractiveColorSwatchBandEnabled && (
        <ColorSwatches
          productPartNo={productPartNo}
          TCPSwatches={TCPSwatches}
          onQuickViewOpenClick={onQuickViewOpenClick}
          assetHost={assetHostProp}
          assetPath={assetPath}
          saveDataForQv={saveDataForQv}
          swatchImg={swatchImg}
          tcpColor={tcpColor}
          auxDescription={auxDescription}
          relatedSwatchImages={relatedSwatchImages}
          isBundleProduct={isBundleProduct}
          handleViewBundleClick={handleViewBundleClick}
          isNewSwatchUrlEnabled={isNewSwatchUrlEnabled}
          handleMouseEnter={handleHoverOnColorSwatch}
          handleMouseLeave={handleMouseEnter}
          isSLPTile={isSLPTile}
        />
      )}
    </div>
  );
};
ProductImage.propTypes = {
  productName: PropTypes.string,
  imageUrl: PropTypes.string,
  imageBorder: PropTypes.string,
  pdpUrl: PropTypes.shape({
    asPath: PropTypes.string,
    href: PropTypes.string,
    asPathWithoutSlug: PropTypes.string,
  }),
  itemAvailability: PropTypes.shape({
    isSoldOut: PropTypes.bool,
    message: '',
  }),
  isVideoUrl: PropTypes.bool,
  bestSellerBadge: PropTypes.bool,
  topRatedBadge: PropTypes.bool,
  currentProductIndex: PropTypes.number.isRequired,
  preloadImage: PropTypes.bool,
  handleMouseEnter: PropTypes.func,
  handleMouseLeave: PropTypes.func,
  imageConfigWithoutDPR: PropTypes.string,
  isHeroProduct: PropTypes.bool,
};
ProductImage.defaultProps = {
  productName: '',
  pdpUrl: {},
  imageUrl: '',
  imageBorder: '',
  itemAvailability: {
    isSoldOut: false,
  },
  isVideoUrl: false,
  bestSellerBadge: false,
  topRatedBadge: false,
  preloadImage: false,
  handleMouseEnter: () => {},
  handleMouseLeave: () => {},
  imageConfigWithoutDPR: '',
  isHeroProduct: false,
};
export default ProductImage;
